import React, { useState, useEffect } from 'react';
import { api, getStation } from '../api';

const VOICES = [
  { id:'voice_warm_female',    label:'Warm Female',     desc:'Smooth, professional, evening warmth' },
  { id:'voice_energetic_male', label:'Energetic Male',  desc:'Upbeat, engaging, drive-time energy' },
  { id:'voice_neutral',        label:'Neutral / Clean', desc:'Versatile, any format, broadcast-standard' },
  { id:'voice_deep_male',      label:'Deep Male',       desc:'Authoritative, news/talk credibility' },
  { id:'voice_bright_female',  label:'Bright Female',   desc:'Friendly, pop-forward, Top 40 energy' },
  { id:'voice_laid_back',      label:'Laid Back Male',  desc:'Late-night, jazz/RnB, conversational' },
];

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
      <div onClick={onChange} style={{
        width:44, height:24, borderRadius:12, background: checked ? '#6366f1' : '#e2e8f0',
        position:'relative', cursor:'pointer', transition:'background 0.2s'
      }}>
        <div style={{
          position:'absolute', top:2, left: checked ? 22 : 2, width:20, height:20,
          borderRadius:'50%', background:'#fff', transition:'left 0.2s',
          boxShadow:'0 1px 3px rgba(0,0,0,0.2)'
        }} />
      </div>
      <span style={{ fontSize:13, color:'#374151', fontWeight:500 }}>{label}</span>
    </label>
  );
}

export default function AIHost() {
  const station = getStation();
  const [config, setConfig] = useState({
    persona_name: '', persona_brief: '', voice_id: 'voice_neutral',
    active_start: '22:00', active_end: '06:00',
    weather_enabled: true, news_enabled: true, time_checks: true,
    station_ident_interval: 30, news_frequency: 8, weather_frequency: 4,
    content_filter: 'family_safe'
  });
  const [status, setStatus]   = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [activeTab, setActiveTab] = useState('config');
  const [clips, setClips] = useState([]);

  const set = (k,v) => setConfig(p => ({...p,[k]:v}));

  const stationId = station?.id;
  useEffect(() => {
    if (!stationId) { setLoading(false); return; }
    Promise.all([
      api.get(`/broadcast/ai-host/${stationId}/config`).catch(() => null),
      api.get(`/broadcast/ai-host/${stationId}/status`).catch(() => ({ status:'inactive', clips_today:0 })),
      api.get(`/broadcast/ai-host/${stationId}/clips`).catch(() => ({ clips:[] })),
    ]).then(([cfg, sts, cl]) => {
      if (cfg) setConfig(c => ({...c,...cfg}));
      setStatus(sts);
      setClips(cl?.clips || []);
    }).finally(() => setLoading(false));
  }, [stationId]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.post(`/broadcast/ai-host/${station.id}/config`, config);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const toggleSession = async () => {
    try {
      const action = status?.status === 'active' ? 'stop' : 'start';
      await api.post(`/broadcast/ai-host/${station.id}/${action}`, {});
      setStatus(s => ({...s, status: action === 'start' ? 'active' : 'inactive'}));
    } catch(e) { alert(e.message); }
  };

  const Tab = ({ id, label }) => (
    <button onClick={() => setActiveTab(id)} style={{
      padding:'8px 16px', fontSize:13, fontWeight:600, border:'none', cursor:'pointer', borderRadius:8,
      background: activeTab===id ? '#6366f1' : 'transparent',
      color: activeTab===id ? '#fff' : '#64748b'
    }}>{label}</button>
  );

  const inp = (k, label, type='text') => (
    <div>
      <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>{label}</label>
      <input type={type} value={config[k]} onChange={e => set(k, type==='number' ? +e.target.value : e.target.value)}
        style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
        onFocus={e => e.target.style.borderColor='#6366f1'} onBlur={e => e.target.style.borderColor='#e2e8f0'}
      />
    </div>
  );

  if (!station) return (
    <div style={{ textAlign:'center', padding:'60px 20px', background:'#fff', borderRadius:16, border:'2px dashed #e2e8f0' }}>
      <div style={{ fontSize:48, marginBottom:12 }}>🎙️</div>
      <h3 style={{ margin:'0 0 8px', color:'#0f172a' }}>No station selected</h3>
      <p style={{ color:'#64748b', fontSize:14 }}>Go to <strong>My Stations</strong> and set an active station first.</p>
    </div>
  );

  return (
    <div>
      {/* Status banner */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#fff', borderRadius:12, padding:'16px 20px', marginBottom:20, border:'1px solid #f1f5f9', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ width:44, height:44, borderRadius:12, background: status?.status==='active' ? '#f0fdf4' : '#fafafa', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>🎙️</div>
          <div>
            <div style={{ fontWeight:700, fontSize:15, color:'#0f172a' }}>AI Overnight Host · {station.callsign}</div>
            <div style={{ fontSize:12, color:'#64748b' }}>
              {status?.status === 'active' ? `● Live · ${status?.clips_today || 0} clips generated today` : `Scheduled ${config.active_start} – ${config.active_end}`}
            </div>
          </div>
        </div>
        <button onClick={toggleSession} style={{
          padding:'9px 20px', borderRadius:8, border:'none', fontWeight:700, fontSize:13, cursor:'pointer',
          background: status?.status==='active' ? '#fef2f2' : 'linear-gradient(135deg,#6366f1,#4f46e5)',
          color: status?.status==='active' ? '#dc2626' : '#fff'
        }}>
          {status?.status === 'active' ? '⏹ Stop Session' : '▶ Start Now'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, background:'#f8fafc', padding:4, borderRadius:10, width:'fit-content' }}>
        <Tab id="config"  label="Configuration" />
        <Tab id="persona" label="Persona & Voice" />
        <Tab id="clips"   label="Recent Clips" />
      </div>

      {activeTab === 'config' && (
        <div style={{ background:'#fff', borderRadius:12, padding:24, border:'1px solid #f1f5f9' }}>
          <h3 style={{ margin:'0 0 18px', fontSize:15, fontWeight:700, color:'#0f172a' }}>Scheduling & Content</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
            {inp('active_start', 'Session Start Time', 'time')}
            {inp('active_end',   'Session End Time',   'time')}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:20 }}>
            <Toggle checked={config.weather_enabled} onChange={() => set('weather_enabled', !config.weather_enabled)} label="Weather updates (every N songs)" />
            <Toggle checked={config.news_enabled}    onChange={() => set('news_enabled',    !config.news_enabled)}    label="News headline breaks" />
            <Toggle checked={config.time_checks}     onChange={() => set('time_checks',     !config.time_checks)}     label="Time checks (every 20 minutes)" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:20 }}>
            {inp('weather_frequency',      'Weather every N songs',     'number')}
            {inp('news_frequency',         'News every N songs',        'number')}
            {inp('station_ident_interval', 'Station ident every N min', 'number')}
          </div>
          <div>
            <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Content Filter</label>
            <select value={config.content_filter} onChange={e => set('content_filter', e.target.value)} style={{ padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none' }}>
              <option value="family_safe">Family Safe (all ages)</option>
              <option value="standard">Standard (general audience)</option>
              <option value="after_watershed">After Watershed (adult content allowed)</option>
            </select>
          </div>
        </div>
      )}

      {activeTab === 'persona' && (
        <div style={{ background:'#fff', borderRadius:12, padding:24, border:'1px solid #f1f5f9' }}>
          <h3 style={{ margin:'0 0 6px', fontSize:15, fontWeight:700, color:'#0f172a' }}>Host Persona</h3>
          <p style={{ margin:'0 0 18px', fontSize:13, color:'#64748b' }}>Define your AI host's personality. This is injected as context into every script generated.</p>
          <div style={{ display:'flex', flexDirection:'column', gap:14, marginBottom:20 }}>
            {inp('persona_name','Host Name (e.g. "Jake" or "Lexi")')}
            <div>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 }}>Persona Brief</label>
              <textarea value={config.persona_brief} onChange={e => set('persona_brief', e.target.value)}
                rows={5} placeholder="Describe your host's personality, tone, vocabulary style, topics to avoid. Example: Jake is a laid-back, warm evening host on WKRS Memphis. He's funny without trying too hard, keeps things conversational, and always knows what's going on in the city."
                style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box' }}
                onFocus={e => e.target.style.borderColor='#6366f1'} onBlur={e => e.target.style.borderColor='#e2e8f0'}
              />
            </div>
          </div>
          <h4 style={{ margin:'0 0 12px', fontSize:13, fontWeight:700, color:'#374151' }}>Select Voice Preset</h4>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
            {VOICES.map(v => (
              <button key={v.id} onClick={() => set('voice_id', v.id)} style={{
                padding:14, borderRadius:10, border:`2px solid ${config.voice_id===v.id ? '#6366f1' : '#e2e8f0'}`,
                background: config.voice_id===v.id ? '#eef2ff' : '#fff', cursor:'pointer', textAlign:'left'
              }}>
                <div style={{ fontSize:13, fontWeight:700, color: config.voice_id===v.id ? '#4f46e5' : '#0f172a', marginBottom:3 }}>{v.label}</div>
                <div style={{ fontSize:11, color:'#64748b' }}>{v.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'clips' && (
        <div style={{ background:'#fff', borderRadius:12, padding:24, border:'1px solid #f1f5f9' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:700, color:'#0f172a' }}>Recent AI Clips</h3>
          {clips.length === 0 ? (
            <p style={{ fontSize:13, color:'#94a3b8', textAlign:'center', padding:'30px 0' }}>No clips generated yet. Start a session to see clips here.</p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {clips.map((c, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 16px', borderRadius:10, background:'#f8fafc', border:'1px solid #f1f5f9' }}>
                  <div style={{ width:36, height:36, borderRadius:8, background:'#eef2ff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
                    {c.clip_type === 'weather' ? '🌤️' : c.clip_type === 'news' ? '📰' : c.clip_type === 'ident' ? '📡' : '🎵'}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'#0f172a', textTransform:'capitalize' }}>{c.clip_type} clip</div>
                    <div style={{ fontSize:12, color:'#94a3b8' }}>{c.script_text?.slice(0,80) || 'No preview'}…</div>
                  </div>
                  <div style={{ fontSize:11, color:'#94a3b8', whiteSpace:'nowrap' }}>{c.duration_seconds}s · {new Date(c.generated_at).toLocaleTimeString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save bar */}
      {activeTab !== 'clips' && (
        <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end', gap:10 }}>
          {saved && <span style={{ color:'#15803d', fontSize:13, fontWeight:600, alignSelf:'center' }}>✓ Saved</span>}
          <button onClick={save} disabled={saving} style={{ padding:'10px 24px', borderRadius:8, border:'none', background: saving?'#c7d2fe':'linear-gradient(135deg,#6366f1,#4f46e5)', color:'#fff', fontWeight:700, fontSize:13, cursor: saving?'not-allowed':'pointer' }}>
            {saving ? 'Saving…' : 'Save Configuration'}
          </button>
        </div>
      )}
    </div>
  );
}
